import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import EmailLink from '@/components/ui/EmailLink.tsx';
import { ROUTES } from '@/config/routes';
import { openCookieConsent } from '@/lib/cookie-consent';
import { Cookie, Mail, Settings } from 'lucide-react';

const cookiesTable = [
  { nom: 'bc_session', type: 'Essentiel', finalite: 'Maintien de la session utilisateur connecté', duree: '24 heures' },
  { nom: 'bc_csrf', type: 'Essentiel', finalite: 'Protection contre les attaques CSRF', duree: 'Session' },
  { nom: 'bc_consent', type: 'Essentiel', finalite: 'Mémorisation du choix de consentement cookies', duree: '13 mois' },
  { nom: 'bc_lang', type: 'Fonctionnel', finalite: 'Mémorisation de la langue préférée', duree: '12 mois' },
  { nom: 'bc_theme', type: 'Fonctionnel', finalite: "Mémorisation des préférences d'affichage", duree: '12 mois' },
  // Ajouter les cookies Google Analytics ci-dessous UNIQUEMENT quand GA est activé,
  // en remplaçant XXXXXXX par votre vrai ID de propriété GA4 :
  // { nom: '_ga', type: 'Analytique', finalite: 'Distinction des utilisateurs (Google Analytics)', duree: '13 mois' },
  // { nom: '_ga_XXXXXXX', type: 'Analytique', finalite: "Conservation de l'état de la session (Google Analytics)", duree: '13 mois' },
  // { nom: '_gid', type: 'Analytique', finalite: 'Distinction des utilisateurs (Google Analytics)', duree: '24 heures' },
];

const typeBadge: Record<string, string> = {
  Essentiel: 'bg-emerald-100 text-emerald-700',
  Fonctionnel: 'bg-blue-100 text-blue-700',
  Analytique: 'bg-amber-100 text-amber-700',
};

export default function Cookies() {
  return (
    <>
      <PageMeta
        title='Politique de cookies'
        description='Politique de gestion des cookies de BoulesConnect : cookies essentiels, analytiques et de personnalisation. Gérez vos préférences.'
        robots='noindex, follow'
      />

      {/* Hero */}
      <section className='pt-40 pb-16 px-6'>
        <div className='max-w-4xl mx-auto text-center'>
          <div className='inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full mb-6'>
            <Cookie className='w-4 h-4 text-blue-600' />
            <span className='text-xs font-bold text-blue-700 uppercase tracking-wider'>Cookies</span>
          </div>
          <h1 className='text-4xl md:text-5xl font-extrabold text-slate-900 mb-4'>Politique de Cookies</h1>
          <p className='text-lg text-slate-500'>Dernière mise à jour : 20 avril 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className='px-6 pb-20'>
        <div className='max-w-4xl mx-auto space-y-8'>
          {/* Qu'est-ce qu'un cookie */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-4'>Qu'est-ce qu'un cookie ?</h2>
            <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
              <p>
                Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, smartphone) lors
                de votre visite sur un site web. Il permet au site de mémoriser certaines informations relatives à votre
                navigation, afin de faciliter vos visites ultérieures et de rendre le site plus convivial.
              </p>
              <p>
                Les cookies ne contiennent pas d'informations permettant de vous identifier personnellement. Ils sont
                stockés sur votre terminal pour une durée déterminée et sont accessibles uniquement par le serveur qui
                les a déposés.
              </p>
              <p>
                Conformément à la directive européenne 2002/58/CE dite "e-Privacy" et aux recommandations de la CNIL,
                nous vous informons de l'utilisation de cookies sur la plateforme BoulesConnect et vous permettons de
                les accepter ou de les refuser.
              </p>
            </div>
          </div>

          {/* Cookies utilisés */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-6'>Cookies utilisés</h2>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='bg-slate-50'>
                    <th className='px-4 py-3 text-left font-bold text-slate-900 rounded-tl-xl'>Nom</th>
                    <th className='px-4 py-3 text-left font-bold text-slate-900'>Type</th>
                    <th className='px-4 py-3 text-left font-bold text-slate-900'>Finalité</th>
                    <th className='px-4 py-3 text-left font-bold text-slate-900 rounded-tr-xl'>Durée</th>
                  </tr>
                </thead>
                <tbody>
                  {cookiesTable.map((cookie, i) => (
                    <tr key={cookie.nom} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className='px-4 py-3 font-mono text-xs font-medium text-slate-900'>{cookie.nom}</td>
                      <td className='px-4 py-3'>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeBadge[cookie.type]}`}>
                          {cookie.type}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-slate-600'>{cookie.finalite}</td>
                      <td className='px-4 py-3 text-slate-500'>{cookie.duree}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='mt-6 grid md:grid-cols-3 gap-4'>
              <div className='p-4 bg-emerald-50 rounded-2xl border border-emerald-100'>
                <h3 className='font-bold text-emerald-900 text-sm mb-1'>Cookies essentiels</h3>
                <p className='text-xs text-emerald-700'>
                  Indispensables au fonctionnement du site. Ils ne peuvent pas être désactivés.
                </p>
              </div>
              <div className='p-4 bg-blue-50 rounded-2xl border border-blue-100'>
                <h3 className='font-bold text-blue-900 text-sm mb-1'>Cookies fonctionnels</h3>
                <p className='text-xs text-blue-700'>Améliorent votre expérience en mémorisant vos préférences.</p>
              </div>
              <div className='p-4 bg-amber-50 rounded-2xl border border-amber-100'>
                <h3 className='font-bold text-amber-900 text-sm mb-1'>Cookies analytiques</h3>
                <p className='text-xs text-amber-700'>
                  Nous aident à comprendre comment vous utilisez le site pour l'améliorer.
                </p>
              </div>
            </div>
          </div>

          {/* Gestion des cookies */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <div className='flex items-center gap-3 mb-6'>
              <div className='w-10 h-10 rounded-2xl bg-purple-50 flex items-center justify-center'>
                <Settings className='w-5 h-5 text-purple-600' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>Gestion des cookies</h2>
            </div>
            <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
              <p>
                Lors de votre première visite sur BoulesConnect, un bandeau d'information vous permet d'accepter ou de
                refuser les cookies non essentiels. Vous pouvez modifier vos choix à tout moment.
              </p>
              <p>
                <strong className='text-slate-900'>Via notre plateforme :</strong> Cliquez sur le bouton ci-dessous ou
                sur "Gérer les cookies" en bas de chaque page pour modifier vos préférences.
              </p>
              <button
                onClick={openCookieConsent}
                className='inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700'
              >
                <Cookie className='h-4 w-4' />
                Gérer mes cookies
              </button>
              <p>
                <strong className='text-slate-900'>Via votre navigateur :</strong> Vous pouvez également configurer
                votre navigateur pour accepter, refuser ou supprimer les cookies. Voici les liens vers les instructions
                des principaux navigateurs :
              </p>
              <ul className='space-y-2 ml-4'>
                <li className='flex items-start gap-2'>
                  <span className='w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 shrink-0' />
                  <a
                    href='https://support.google.com/chrome/answer/95647'
                    className='text-blue-600 hover:underline'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Google Chrome
                  </a>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 shrink-0' />
                  <a
                    href='https://support.mozilla.org/fr/kb/activer-desactiver-cookies'
                    className='text-blue-600 hover:underline'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Mozilla Firefox
                  </a>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 shrink-0' />
                  <a
                    href='https://support.apple.com/fr-fr/guide/safari/sfri11471'
                    className='text-blue-600 hover:underline'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Safari
                  </a>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 shrink-0' />
                  <a
                    href='https://support.microsoft.com/fr-fr/microsoft-edge'
                    className='text-blue-600 hover:underline'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Microsoft Edge
                  </a>
                </li>
              </ul>
              <p className='mt-3 p-4 bg-amber-50 rounded-2xl border border-amber-100 text-amber-800'>
                <strong>Attention :</strong> La désactivation des cookies essentiels peut empêcher le bon fonctionnement
                de certaines fonctionnalités de la plateforme, notamment la connexion à votre compte.
              </p>
            </div>
          </div>

          {/* Cookies tiers */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-4'>Cookies tiers</h2>
            <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
              <p>La plateforme BoulesConnect peut déposer des cookies tiers provenant des services suivants :</p>
              <div className='grid md:grid-cols-2 gap-4 mt-4'>
                <div className='p-4 bg-slate-50 rounded-2xl'>
                  <h3 className='font-bold text-slate-900 mb-2'>Stripe</h3>
                  <p className='text-xs text-slate-500'>
                    Notre prestataire de paiement utilise des cookies pour sécuriser les transactions et prévenir la
                    fraude. Ces cookies sont déposés uniquement lors d'un processus de paiement.
                  </p>
                  <a
                    href='https://stripe.com/fr/privacy'
                    className='text-xs text-blue-600 hover:underline mt-2 inline-block'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Politique de confidentialité Stripe
                  </a>
                </div>
                {/* Bloc Google Analytics à réactiver UNIQUEMENT quand GA est déployé : */}
                {/* <div className='p-4 bg-slate-50 rounded-2xl'>
                  <h3 className='font-bold text-slate-900 mb-2'>Google Analytics</h3>
                  <p className='text-xs text-slate-500'>
                    Service d'analyse d'audience fourni par Google. Ces cookies sont déposés uniquement si vous avez
                    consenti aux cookies analytiques. Les données sont anonymisées.
                  </p>
                  <a href='https://policies.google.com/privacy' className='text-xs text-blue-600 hover:underline mt-2 inline-block' target='_blank' rel='noopener noreferrer'>
                    Politique de confidentialité Google
                  </a>
                </div> */}
              </div>
            </div>
          </div>

          {/* Modification */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-4'>Modification de cette politique</h2>
            <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
              <p>
                MADSAAS SAS se réserve le droit de modifier la présente politique de cookies à tout moment afin de se
                conformer aux évolutions réglementaires ou techniques. Toute modification sera publiée sur cette page
                avec une nouvelle date de mise à jour.
              </p>
              <p>
                En cas de modification substantielle, un nouveau consentement pourra vous être demandé via le bandeau de
                cookies.
              </p>
            </div>
          </div>

          {/* Contact */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <div className='flex items-center gap-3 mb-6'>
              <div className='w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center'>
                <Mail className='w-5 h-5 text-blue-600' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>Contact</h2>
            </div>
            <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
              <p>
                Pour toute question relative à l'utilisation des cookies sur BoulesConnect, vous pouvez nous contacter :
              </p>
              <div className='p-4 bg-slate-50 rounded-2xl'>
                <p>
                  <strong className='text-slate-900'>MADSAAS SAS</strong>
                </p>
                <p>
                  Email : <EmailLink user='dpo' domain='boulesconnect.fr' className='text-blue-600 hover:underline' />
                </p>
              </div>
              <p className='mt-2'>
                Consultez également notre{' '}
                <Link to={ROUTES.POLITIQUE_RGPD} className='text-blue-600 hover:underline'>
                  Politique de Confidentialité et RGPD
                </Link>{' '}
                pour plus d'informations sur le traitement de vos données personnelles.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
